import React from 'react';
import Header from './Header';
import Footer from './Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-offWhite text-charcoal">
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-playfair font-bold mb-8 text-charcoal">Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none text-charcoal/80">
          <p className="text-lg mb-6">
            <strong>Last Updated: April 2026</strong>
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">1. Introduction</h2>
            <p className="mb-4">
              Wako ("we," "us," "our," or "Company") is a premium dating and matchmaking platform designed for meaningful connections among singles in Zambia and beyond. We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, mobile application, and related services (collectively, the "Service").
            </p>
            <p>
              Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do not use our Service. By accessing and using Wako, you acknowledge that you have read, understood, and agree to be bound by all the provisions of this Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold text-charcoal mb-2">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, phone number, date of birth, gender, sexual orientation, location, and profile information.</li>
              <li><strong>Dating Preferences:</strong> We collect information about your dating preferences, interests, relationship goals (serious, casual, friendship), and what you're looking for in a match.</li>
              <li><strong>Profile Content:</strong> We collect profile photos, bio, interests, and other content you share to help create meaningful matches.</li>
              <li><strong>Communications:</strong> We collect messages, photos, and other content you share through our messaging service.</li>
              <li><strong>Payment Information:</strong> If you purchase a premium subscription, we collect payment details through our payment processors (we do not store full credit card information).</li>
              <li><strong>Customer Support:</strong> We collect information you provide when contacting our support team.</li>
            </ul>

            <h3 className="text-xl font-semibold text-charcoal mb-2">2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Device Information:</strong> We collect information about your device, including hardware model, operating system, unique device identifiers, and mobile network information.</li>
              <li><strong>Usage Data:</strong> We collect information about your interactions with our Service, including profiles viewed, matches made, likes sent/received, and features used.</li>
              <li><strong>Location Information:</strong> We collect location data to provide distance-based matching and location features. You can control location permissions through your device settings.</li>
              <li><strong>Matching Algorithms:</strong> We collect interaction data to improve our matching algorithms and provide better recommendations.</li>
              <li><strong>Cookies and Similar Technologies:</strong> We use cookies, web beacons, and similar tracking technologies to enhance your experience.</li>
            </ul>

            <h3 className="text-xl font-semibold text-charcoal mb-2">2.3 Information from Third Parties</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Social media platforms (if you connect your accounts)</li>
              <li>Analytics providers</li>
              <li>Payment processors</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">We use the collected information for various purposes, including:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Creating and maintaining your profile and matching with compatible users</li>
              <li>Powering our matchmaking algorithms to suggest meaningful connections</li>
              <li>Providing real-time messaging and communication features</li>
              <li>Processing premium subscription payments and transactions</li>
              <li>Sending notifications about matches, messages, and platform updates (with your consent)</li>
              <li>Responding to your inquiries and providing customer support</li>
              <li>Monitoring and preventing fraudulent activity, abuse, and safety violations</li>
              <li>Improving and personalizing your experience on the platform</li>
              <li>Complying with legal obligations and safety requirements</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">4. Information Sharing and Disclosure</h2>
            <p className="mb-4">We may share your information in the following circumstances:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Service Providers:</strong> We share information with vendors who assist in hosting, payment processing, analytics, and customer support.</li>
              <li><strong>User Profiles:</strong> Your profile information (excluding sensitive details) is visible to other users to facilitate meaningful connections. You control which information is displayed.</li>
              <li><strong>Matched Users:</strong> When you match with another user, basic profile information is shared to enable communication.</li>
              <li><strong>Safety & Compliance:</strong> We may disclose information to prevent, investigate, or address fraud, security, or technical issues.</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information when required by law or in response to legal process.</li>
              <li><strong>Business Transfers:</strong> In case of merger, acquisition, or asset sale, your information may be transferred.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">5. Security of Your Information</h2>
            <p className="mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information and ensure a safe dating environment, including:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication mechanisms</li>
              <li>Regular security audits and monitoring</li>
              <li>Restricted access to sensitive personal data</li>
              <li>Photo and profile verification measures</li>
            </ul>
            <p>
              However, no method of transmission over the Internet or electronic storage is completely secure. We encourage you to use strong passwords and enable two-factor authentication where available.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">6. Your Privacy Rights</h2>
            <p className="mb-4">Depending on your jurisdiction, you may have the following rights:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Access:</strong> You have the right to access your personal information.</li>
              <li><strong>Correction:</strong> You can request correction of inaccurate information.</li>
              <li><strong>Deletion:</strong> You may request deletion of your information (subject to certain exceptions).</li>
              <li><strong>Data Portability:</strong> You may request a copy of your data in a portable format.</li>
              <li><strong>Opt-Out:</strong> You can opt-out of marketing communications at any time.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">7. Cookies and Tracking Technologies</h2>
            <p className="mb-4">
              We use cookies and similar tracking technologies to track activity on our Service and hold certain information. You can instruct your browser to refuse all cookies or alert you when cookies are being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">8. Children's Privacy</h2>
            <p className="mb-4">
              Our Service is not intended for children under 18 years old. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information and terminate the child's account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">9. Third-Party Links</h2>
            <p className="mb-4">
              Our Service may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">10. Safety & Reporting</h2>
            <p className="mb-4">
              Wako is committed to providing a safe dating environment. We encourage you to report any inappropriate behavior, harassment, or safety concerns to our support team immediately. We take all reports seriously and will take appropriate action.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">11. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us.
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
